import { AvatarBlock, Button, Heading, Spacer, Steps } from '@ksyos/design-system';
import { useLocation, useNavigate } from '@ksyos/router';
import { pick } from 'lodash';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { ValidationErrorCode } from '@common/enums/validationErrorCode';
import { getDisplayName } from '@common/utils/getDisplayName';
import { KgbSuccessPage } from '@frontend/components/elements/KgbSuccessPage';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { RightButtons } from '@frontend/components/elements/RightButtons';
import { OverlayTemplate } from '@frontend/components/templates/OverlayTemplate';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { useAbortableFetch, useValidationErrors } from '@frontend/utils/hooks';
import { emptyValidationErrors } from '@frontend/utils/validation';
import type { IAddKsyosEmployeeState, IKsyosAccountDetails } from './addKsyosEmployeeState';
import { Account, getInitialAccountDetails } from './Account';
import { Profile } from './Profile';

enum AddKsyosEmployeeStep {
    profile,
    account,
}

export function AddKsyosEmployee() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [isSaving, setIsSaving] = useState(false);
    const [currentStep, setCurrentStep] = useState(AddKsyosEmployeeStep.profile);
    const [isCurrentStepValid, setIsCurrentStepValid] = useState(false);
    const [addKsyosEmployeeState, setAddKsyosEmployeeState] = useState<IAddKsyosEmployeeState>({});
    const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);
    const [serverErrors, setServerErrors] = useValidationErrors();
    const [isSavedSuccessfully, setIsSavedSuccessfully] = useState<boolean>(false);
    const [isUserNameChangedByUser, setIsUserNameChangedByUser] = useState(false);
    const abortableFetch = useAbortableFetch();

    assertHasAuthorization(ManagerAuthorization.adminsAddEdit);

    useEffect(() => {
        if (!isUserNameChangedByUser) {
            generateUserName();
        }
    }, [addKsyosEmployeeState.profileDetails?.nameGiven, addKsyosEmployeeState.profileDetails?.nameFamily]);

    const steps = [{ title: translateKey('common.profile') }, { title: translateKey('profile.account') }];

    const showStep = [
        <Profile
            key="profile"
            {...addKsyosEmployeeState}
            onUpdateIsValid={handleUpdateIsValid}
            onUpdateManagerRoleId={(value) => handleChange('managerRoleId', value)}
            onUpdateProfileDetails={(value) => handleChange('profileDetails', value)}
        />,
        <Account
            key="account"
            {...addKsyosEmployeeState}
            onUpdateAccountDetails={(value) => handleChange('accountDetails', value)}
            onUpdateIsValid={handleUpdateIsValid}
            serverErrors={serverErrors}
        />,
    ];

    if (isSavedSuccessfully) {
        return (
            <OverlayTemplate>
                <KgbSuccessPage
                    buttonText={translateKey('practitioner.returnToOrganizationOverview')}
                    linkTo={getKsyosOrganizationUrl()}
                    text={translateKey(
                        'employment.employmentAdded',
                        getDisplayName(addKsyosEmployeeState.profileDetails!, false, 'nameGivenNameFamily'),
                        'Ksyos'
                    )}
                    title={translateKey('organization.successful')}
                />
            </OverlayTemplate>
        );
    }

    return (
        <OverlayTemplate
            leftButtons={
                <Button
                    isDisabled={isSaving}
                    onClick={() => setShowConfirmCancelModal(true)}
                    variant="outline"
                >
                    {translateKey('common.cancelling')}
                </Button>
            }
            rightButtons={
                <RightButtons
                    currentStep={currentStep}
                    isDisabled={isCurrentStepValid === false || isSaving}
                    nextStepLabel={currentStep === steps.length - 1 ? translateKey('common.saveButton') : undefined}
                    onClickNextStep={handleClickNextStep}
                    onClickPreviousStep={handleClickPreviousStep}
                />
            }
        >
            <Spacer spacing="large">
                <Heading size="xlarge">{translateKey('organization.addStaffMember')}</Heading>
                <AvatarBlock
                    backgroundColor="white"
                    icon="organization"
                    shape="square"
                    size="medium"
                    title="Ksyos"
                />
            </Spacer>

            <Steps
                activeStep={currentStep}
                sx={{ mb: 6 }}
            >
                {steps.map((step, index) => (
                    <Steps.Step
                        key={step.title}
                        label={step.title}
                        onClick={() => setCurrentStep(index)}
                    />
                ))}
            </Steps>

            {showStep[currentStep]}

            <ConfirmationModal
                handleNo={() => setShowConfirmCancelModal(false)}
                handleYes={() => navigate(-1)}
                isOpen={showConfirmCancelModal}
                question={translateKey('practitioner.confirmCancelMessage')}
                title={translateKey('practitioner.confirmCancelAddKsyosEmployeeTitle')}
            />
        </OverlayTemplate>
    );

    async function generateUserName() {
        if (addKsyosEmployeeState.profileDetails?.nameFamily) {
            const result = await abortableFetch.postJson(
                '/api/practitioners/generateUserName',
                pick(addKsyosEmployeeState.profileDetails, ['nameGiven', 'nameInfix', 'nameFamily'])
            );

            setAddKsyosEmployeeState((currentState) => ({
                ...currentState,
                accountDetails: {
                    ...(currentState.accountDetails ?? getInitialAccountDetails()),
                    userName: result.json.userName,
                },
            }));
        }
    }

    function getKsyosOrganizationUrl() {
        return pathname.substring(0, pathname.lastIndexOf('/'));
    }

    async function handleClickNextStep() {
        if (!isCurrentStepValid) {
            return;
        }

        if (currentStep < steps.length - 1) {
            setCurrentStep((step) => step + 1);
        } else {
            await saveEmployee();
        }
    }

    function handleClickPreviousStep() {
        setCurrentStep((step) => step - 1);
    }

    function handleChange<K extends keyof IAddKsyosEmployeeState>(name: K, value: IAddKsyosEmployeeState[K]) {
        handleUserNameChange<K>(value);
        setAddKsyosEmployeeState((currentState) => ({ ...currentState, [name]: value }));
    }

    function handleUserNameChange<K extends keyof IAddKsyosEmployeeState>(value: IAddKsyosEmployeeState[K]) {
        if (!(value as IKsyosAccountDetails)?.userName) {
            return;
        }

        const userName = (value as IKsyosAccountDetails).userName;

        if (userName && userName !== addKsyosEmployeeState?.accountDetails?.userName) {
            setIsUserNameChangedByUser(true);
        }
    }

    function handleUpdateIsValid(isValid: boolean) {
        setIsCurrentStepValid(isValid);
    }

    async function saveEmployee() {
        setServerErrors(emptyValidationErrors);
        setIsSaving(true);

        const result = await abortableFetch.fetchJson(
            '/api/practitioners/ksyosEmployee',
            'POST',
            addKsyosEmployeeState,
            [ValidationErrorCode.USERNAME_EXISTS]
        );

        if (result.errorCode === ValidationErrorCode.USERNAME_EXISTS) {
            setServerErrors(serverErrors.set('userName', result.errorCode));
        } else {
            // resetBlock();
            setIsSavedSuccessfully(true);
        }

        setIsSaving(false);
    }
}
